import { buildApiUrl, buildAssetUrl } from '../../utils/api';
import { getProductSlug } from '../../utils/productSlug';
import { getCookie } from '../../utils/cookies';
import { getActivePincode } from '../../utils/location';

const productDetailCache = new Map();

const normalizeProductDetail = (data) => {
  if (!data) return null;
  // gallery_images are usually objects: { product_image_id, image_path, position }.
  const galleryPaths = [...(data.gallery_images || [])]
    .sort((a, b) => (a.position ?? 99) - (b.position ?? 99))
    .map((image) => (
      typeof image === 'string'
        ? image
        : image.image_path || image.image || image.url || image.path
    ));

  const allImagePaths = [
    data.image,
    data.product_image,
    data.image1,
    data.image2,
    data.image3,
    data.image4,
    data.image5,
    data.image6,
    ...galleryPaths,
  ].filter(Boolean);

  const images = [...new Set(allImagePaths)].map(buildAssetUrl);

  return {
    ...data,
    id: data.product_id ?? data.id,
    product_id: data.product_id ?? data.id,
    name: data.name ?? data.product_name ?? 'Product',
    gst: Number.parseFloat(data.gst ?? data.gst_percentage ?? 0) || 0,
    image: images[0] || (data.image ? buildAssetUrl(data.image) : null),
    images,
    is_serviceable: data.is_serviceable ?? null,
    is_in_stock: data.is_in_stock ?? true,
    available_stock: data.available_stock ?? null,
    stock_status: data.stock_status ?? null,
    stock_message: data.stock_message ?? null,
    matched_store_id: data.matched_store_id ?? null,
    variants: (data.variants || []).map((v) => ({
      ...v,
      discount_price: parseFloat(v.discount_price ?? v.price ?? 0),
      original_price: parseFloat(v.original_price ?? v.originalPrice ?? v.discount_price ?? 0),
      is_in_stock: v.is_in_stock ?? data.is_in_stock ?? true,
      available_stock: v.available_stock ?? data.available_stock ?? null,
      stock_status: v.stock_status ?? data.stock_status ?? null,
      stock_message: v.stock_message ?? data.stock_message ?? null,
    })),
  };
};

export const createProductDetailSlice = (set, get) => ({
  productDetail: null,
  productDetail_loading: false,
  productDetail_error: null,

  fetchProductDetail: async (productIdentifier, explicitPincode = null) => {
    if (!productIdentifier) return;
    const lookupKey = String(productIdentifier);

    // Ensure user addresses are fetched if authenticated and not yet in store
    const token = getCookie('authToken');
    if (token && (!get().user_addresses || get().user_addresses.length === 0)) {
      try {
        await get().fetchUserAddresses?.();
      } catch {
        // best-effort
      }
    }

    // Use the same active pincode passed to all other APIs (userLocation / selected location)
    const activePin = getActivePincode(get());
    const resolvedPincode =
      (activePin ? String(activePin).trim() : null) ||
      (explicitPincode ? String(explicitPincode).trim() : null) ||
      (get().userLocation?.pincode ? String(get().userLocation.pincode).trim() : null) ||
      '';

    const cacheKey = `${lookupKey}_${resolvedPincode}`;

    // 1. Instant Cache Check
    if (productDetailCache.has(cacheKey)) {
      const cached = productDetailCache.get(cacheKey);
      set({ productDetail: cached, productDetail_loading: false, productDetail_error: null });
    } else {
      // 2. Instant Store Products Check (render immediately without blocking spinner)
      const existingInStore = get().products?.find(
        (p) =>
          String(p.id ?? p.product_id) === lookupKey ||
          getProductSlug(p) === lookupKey
      );
      if (existingInStore) {
        const initialNormalized = normalizeProductDetail(existingInStore);
        set({ productDetail: initialNormalized, productDetail_loading: false, productDetail_error: null });
      } else {
        set({ productDetail_loading: true, productDetail_error: null });
      }
    }

    // 3. Fast Fetch & Cache in Background
    try {
      const qs = resolvedPincode ? `?pincode=${encodeURIComponent(resolvedPincode)}` : '';
      const endpoint = `/api/v1/customer/product-details/${encodeURIComponent(lookupKey)}${qs}`;
      const token = getCookie('authToken');
      const headers = { 'Accept': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(buildApiUrl(endpoint), { headers });
      if (!res.ok) throw new Error('Failed to fetch product details');
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Failed to fetch product details');

      const normalized = normalizeProductDetail(json.data);
      productDetailCache.set(cacheKey, normalized);
      set({ productDetail: normalized, productDetail_loading: false, productDetail_error: null });
    } catch (err) {
      if (!get().productDetail) {
        set({ productDetail_error: err.message, productDetail_loading: false });
      }
    }
  },

  clearProductDetail: () => set({ productDetail: null, productDetail_error: null }),
});
 
